import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api.js';
import { useAuthStore } from '../lib/auth.js';

export type AuditAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'share_toggle'
  | 'send'
  | 'login'
  | 'invite_accepted';

export const AUDIT_ACTIONS: AuditAction[] = [
  'create',
  'update',
  'delete',
  'share_toggle',
  'send',
  'login',
  'invite_accepted',
];

export type AuditRow = {
  id: string;
  occurred_at: string;
  action: AuditAction;
  resource_type: string;
  resource_id: string | null;
  actor_id: string | null;
  actor_name: string | null;
  actor_email: string | null;
  actor_role: string | null;
  tenant_id: string | null;
  tenant_name: string | null;
  ip_address: string | null;
  metadata: unknown;
  /** Human "what changed" line from the server (may be ''). */
  summary: string;
};

export type AuditListResult = {
  items: AuditRow[];
  total: number;
  actors: { id: string; name: string }[];
};

export type AuditFilters = {
  actorId?: string;
  action?: AuditAction;
  q?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
};

function toQueryString(f: AuditFilters): string {
  const p = new URLSearchParams();
  if (f.actorId) p.set('actorId', f.actorId);
  if (f.action) p.set('action', f.action);
  if (f.q) p.set('q', f.q);
  if (f.from) p.set('from', f.from);
  if (f.to) p.set('to', f.to);
  if (f.limit !== undefined) p.set('limit', String(f.limit));
  if (f.offset !== undefined) p.set('offset', String(f.offset));
  return p.toString();
}

export function useAuditLog(filters: AuditFilters) {
  const qs = toQueryString(filters);
  return useQuery({
    queryKey: ['admin', 'audit-log', qs],
    queryFn: () => apiGet<AuditListResult>(`/admin/audit-log${qs ? `?${qs}` : ''}`),
    staleTime: 30_000,
  });
}

/**
 * Download the filtered audit log as CSV. Uses the in-memory access token and
 * triggers a browser download. Filters mirror the list view (no pagination).
 */
export async function downloadAuditCsv(filters: AuditFilters): Promise<void> {
  const { limit: _limit, offset: _offset, ...rest } = filters;
  const qs = toQueryString(rest);
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`/api/v1/admin/audit-log/export${qs ? `?${qs}` : ''}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'audit-log.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
